import { RepresentanteLegalBase, Endereco, DocumentInfo, TipoInvestidor } from 'common/genericTypes';
import { typedAction } from 'store/modules/base';
import { Moment } from 'moment';

export interface RepresentanteLegalInvestidorState extends RepresentanteLegalBase {}

export interface EnderecoState extends Endereco {
  numero: string;
}

export interface InformacoesPrincipaisState {
  cpfCnpj: string;
  isPj: boolean;
  razaoSocial: string;
  profissao: string;
  cnaePrincipal?: string;
  nacionalidade: string;
  dataNascimento: Moment | null;
  investidorPoliticamenteExposto: boolean;
  vinculadoCustodiante: boolean;
  tipo: TipoInvestidor;
  rendaMensalDeclarada: number;
  patrimonioDeclarado: number;
}

export interface InformacoesParaContatoState {
  telefones: Array<string>;
  email: string;
}

export interface InformacoesBancariasState {
  banco: string;
  ispbBanco: string;
  agencia: string;
  agenciaDigito: string;
  conta: string;
  contaDigito: string;
}

export interface InvestidorState {
  id?: string;
  informacoesPrincipais: InformacoesPrincipaisState;
  endereco: EnderecoState;
  informacoesParaContato: InformacoesParaContatoState;
  informacoesBancarias: InformacoesBancariasState;
  representantesLegais: Array<RepresentanteLegalInvestidorState>;
  documentos: Array<DocumentInfo>;
  documentosExcluir: Array<DocumentInfo>;
  highlightErrors: boolean;
}

interface InformacoesPrincipaisField {
  name: keyof InformacoesPrincipaisState;
  value: string | number | null | Array<string> | boolean | Moment | TipoInvestidor;
}

interface EnderecoField {
  name: keyof EnderecoState;
  value: string;
}

interface InformacoesParaContatoField {
  name: keyof InformacoesParaContatoState;
  value: string | Array<string>;
}

interface InformacoesBancariasField {
  name: keyof InformacoesBancariasState;
  value: string;
}

export const initialInformacoesPrincipaisState: InformacoesPrincipaisState = {
  cpfCnpj: '',
  isPj: false,
  razaoSocial: '',
  profissao: '',
  nacionalidade: '',
  dataNascimento: null,
  investidorPoliticamenteExposto: false,
  vinculadoCustodiante: false,
  tipo: TipoInvestidor.Nenhum,
  rendaMensalDeclarada: 0,
  patrimonioDeclarado: 0,
};

export const initialEnderecoState: EnderecoState = {
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
};

export const initialInformacoesParaContatoState: InformacoesParaContatoState = {
  telefones: [],
  email: '',
};

export const initialInformacoesBancariasState: InformacoesBancariasState = {
  banco: '',
  ispbBanco: '',
  agencia: '',
  agenciaDigito: '',
  conta: '',
  contaDigito: '',
};

export const initialInvestidorState: InvestidorState = {
  informacoesPrincipais: initialInformacoesPrincipaisState,
  endereco: initialEnderecoState,
  informacoesParaContato: initialInformacoesParaContatoState,
  informacoesBancarias: initialInformacoesBancariasState,
  representantesLegais: [],
  documentos: [],
  documentosExcluir: [],
  highlightErrors: false,
};

export const updateInvestidor = (investidorState: InvestidorState) => {
  return typedAction('investidor/UPDATE', investidorState);
};

export const resetInvestidor = () => {
  return typedAction('investidor/RESET');
};

export const updateInformacoesPrincipaisField = (field: InformacoesPrincipaisField) => {
  return typedAction('investidor/informacoesPrincipais/UPDATEFIELD', field);
};

export const updateInformacoesPrincipais = (informacoesPrincipaisState: InformacoesPrincipaisState) => {
  return typedAction('investidor/informacoesPrincipais/UPDATE', informacoesPrincipaisState);
};

export const resetInformacoesPrincipais = () => {
  return typedAction('investidor/informacoesPrincipais/RESET');
};

export const updateEnderecoField = (field: EnderecoField) => {
  return typedAction('investidor/endereco/UPDATEFIELD', field);
};

export const updateRepresentanteLegais = (representantes: RepresentanteLegalInvestidorState[]) => {
  return typedAction('investidor/representantes/UPDATE', representantes);
};

export const updateEndereco = (enderecoState: EnderecoState) => {
  return typedAction('investidor/endereco/UPDATE', enderecoState);
};

export const resetEndereco = () => {
  return typedAction('investidor/endereco/RESET');
};

export const updateInformacoesParaContatoField = (field: InformacoesParaContatoField) => {
  return typedAction('investidor/informacoesParaContato/UPDATEFIELD', field);
};

export const updateInformacoesParaContato = (informacoesParaContatoState: InformacoesParaContatoState) => {
  return typedAction('investidor/informacoesParaContato/UPDATE', informacoesParaContatoState);
};

export const resetInformacoesParaContato = () => {
  return typedAction('investidor/informacoesParaContato/RESET');
};

export const updateInformacoesBancariasField = (field: InformacoesBancariasField) => {
  return typedAction('investidor/informacoesBancarias/UPDATEFIELD', field);
};

export const updateInformacoesBancarias = (informacoesBancariasState: InformacoesBancariasState) => {
  return typedAction('investidor/informacoesBancarias/UPDATE', informacoesBancariasState);
};

export const resetInformacoesBancarias = () => {
  return typedAction('investidor/informacoesBancarias/RESET');
};

export const updateRepresentantesLegais = (representantes: RepresentanteLegalInvestidorState[]) => {
  return typedAction('investidor/representantesLegais/UPDATE', representantes);
};

export const updateDocumentos = (documents: DocumentInfo[]) => {
  return typedAction('investidor/documentos/UPDATE', documents);
};

export const updateDocumentosExcluir = (documents: DocumentInfo[]) => {
  return typedAction('investidor/documentosExcluir/UPDATE', documents);
};

export const hightlightErrors = (payload: boolean) => {
  return typedAction('investidor/HIGHLIGHT_ERRORS', payload);
};

type InvestidorRegistroAction = ReturnType<
  | typeof updateInvestidor
  | typeof resetInvestidor
  | typeof updateInformacoesPrincipaisField
  | typeof updateInformacoesPrincipais
  | typeof resetInformacoesPrincipais
  | typeof updateEnderecoField
  | typeof updateEndereco
  | typeof resetEndereco
  | typeof updateInformacoesParaContatoField
  | typeof updateInformacoesParaContato
  | typeof resetInformacoesParaContato
  | typeof updateInformacoesBancariasField
  | typeof updateInformacoesBancarias
  | typeof resetInformacoesBancarias
  | typeof updateRepresentantesLegais
  | typeof updateDocumentos
  | typeof updateDocumentosExcluir
  | typeof hightlightErrors
>;

export function investidorReducer(
  state: InvestidorState = initialInvestidorState,
  action: InvestidorRegistroAction
): InvestidorState {
  switch (action.type) {
    case 'investidor/UPDATE':
      return { ...action.payload };
    case 'investidor/RESET':
      return { ...initialInvestidorState };
    case 'investidor/informacoesPrincipais/UPDATEFIELD':
      return {
        ...state,
        informacoesPrincipais: { ...state.informacoesPrincipais, [action.payload.name]: action.payload.value },
      };
    case 'investidor/informacoesPrincipais/UPDATE':
      return { ...state, informacoesPrincipais: action.payload };
    case 'investidor/informacoesPrincipais/RESET':
      return { ...state, informacoesPrincipais: initialInformacoesPrincipaisState };
    case 'investidor/endereco/UPDATEFIELD':
      return { ...state, endereco: { ...state.endereco, [action.payload.name]: action.payload.value } };
    case 'investidor/endereco/UPDATE':
      return { ...state, endereco: action.payload };
    case 'investidor/endereco/RESET':
      return { ...state, endereco: initialEnderecoState };
    case 'investidor/informacoesParaContato/UPDATEFIELD':
      return {
        ...state,
        informacoesParaContato: { ...state.informacoesParaContato, [action.payload.name]: action.payload.value },
      };
    case 'investidor/informacoesParaContato/UPDATE':
      return { ...state, informacoesParaContato: action.payload };
    case 'investidor/informacoesParaContato/RESET':
      return { ...state, informacoesParaContato: initialInformacoesParaContatoState };
    case 'investidor/informacoesBancarias/UPDATEFIELD':
      return {
        ...state,
        informacoesBancarias: { ...state.informacoesBancarias, [action.payload.name]: action.payload.value },
      };
    case 'investidor/informacoesBancarias/UPDATE':
      return { ...state, informacoesBancarias: action.payload };
    case 'investidor/informacoesBancarias/RESET':
      return { ...state, informacoesBancarias: initialInformacoesBancariasState };
    case 'investidor/representantesLegais/UPDATE':
      return { ...state, representantesLegais: action.payload };
    case 'investidor/documentos/UPDATE':
      return { ...state, documentos: action.payload };
    case 'investidor/documentosExcluir/UPDATE':
      return { ...state, documentosExcluir: action.payload };
    case 'investidor/HIGHLIGHT_ERRORS':
      return { ...state, highlightErrors: action.payload };
    default:
      return state;
  }
}
